import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, ArrowRight } from "lucide-react";
import { useLocale } from "@/i18n/useLocale";
import { useAuth } from "@/context/AuthContext";
import promoEn from "@/data/partner_promo_content.en.json";
import promoAr from "@/data/partner_promo_content.ar.json";

const DISMISS_KEY = "tp_partner_banner_dismissed_v1";

/**
 * Public partner-program announcement bar. Bilingual (EN/AR), dismissible,
 * only rendered when the visitor is logged out. Dismissal is persisted in
 * localStorage so returning visitors don't see it again.
 */
export default function PartnerAnnouncementBanner() {
  const { locale } = useLocale();
  const isAr = String(locale || "en").toLowerCase().startsWith("ar");
  const content = (isAr ? promoAr : promoEn).banner;
  const auth = useAuth() || {};
  const isLoggedIn = !!(auth.user || auth.token);

  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    try { setDismissed(localStorage.getItem(DISMISS_KEY) === "1"); } catch { /* ignore */ }
  }, []);

  if (isLoggedIn || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  return (
    <div
      dir={isAr ? "rtl" : "ltr"}
      role="region"
      aria-label={content.eyebrow}
      data-testid="partner-announcement-banner"
      className="relative isolate w-full border-b border-[#D6A653]/25 bg-gradient-to-r from-[#1A130A] via-[#0E0F14] to-[#1A130A]"
    >
      <div className="mx-auto flex max-w-[1320px] items-center gap-4 px-4 py-2.5 text-sm sm:px-8 lg:px-12">
        <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-[#D6A653]/40 bg-[#D6A653]/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#E6C787] sm:inline-flex" data-testid="partner-banner-eyebrow">
          <Sparkles className="h-3 w-3" />
          {content.eyebrow}
        </span>

        <p className="min-w-0 flex-1 truncate text-[13px] text-white/85" data-testid="partner-banner-text">
          <span className="font-medium text-white">{content.title}</span>
          <span className="mx-2 text-white/30">·</span>
          <span className="hidden text-white/60 md:inline">{content.body}</span>
        </p>

        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/partners/apply"
            data-testid="partner-banner-cta-primary"
            className="inline-flex items-center gap-1 rounded-full bg-[#D6A653] px-3 py-1 text-[12px] font-medium text-black transition-colors hover:bg-[#c99a49]"
          >
            {content.ctaPrimary}
            <ArrowRight className={`h-3 w-3 ${isAr ? "rotate-180" : ""}`} />
          </Link>
          <Link
            to="/partners"
            data-testid="partner-banner-cta-secondary"
            className="hidden rounded-full border border-white/15 px-3 py-1 text-[12px] text-white/75 hover:bg-white/5 sm:inline-flex"
          >
            {content.ctaSecondary}
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label={content.dismissAria}
            data-testid="partner-banner-dismiss"
            className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-white/40 hover:bg-white/5 hover:text-white/80"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
